var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isValidElement, Component, createRef, cloneElement, Children, Fragment, } from 'react';
import { Announcement, Table as AnvilTable, TableColumn, } from '@servicetitan/design-system';
import { operators as kendoOperators } from '@progress/kendo-react-grid/dist/npm/filterCommon';
import { GridPDFExport as TablePDFExport } from '@progress/kendo-react-pdf';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { observer } from 'mobx-react';
import { AsyncDataSource } from '@servicetitan/data-query';
import { SelectColumnCell, SelectHeaderCell, } from './select-cell/select-cell';
import memoizeOne from 'memoize-one';
import classNames from 'classnames';
import * as Styles from './table.module.css';
const { text: textOperators } = kendoOperators, operators = __rest(kendoOperators, ["text"]);
operators.text = [
    ...textOperators.filter(o => o.operator !== 'isnull' && o.operator !== 'isnotnull'),
];
function isColumnElement(element) {
    if (!isValidElement(element)) {
        return false;
    }
    const elementType = element.type;
    return elementType === TableColumn || elementType.originalType === TableColumn;
}
let Table = class Table extends Component {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "tableState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: this.props.tableState
        });
        Object.defineProperty(this, "ref", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: createRef()
        });
        Object.defineProperty(this, "lastSelection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "selectColumnCell", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observer((props) => (_jsx(SelectColumnCell, Object.assign({}, props, { isRowSelectable: this.tableState.isRowSelectable(props.dataItem), control: this.props.selectionControl }))))
        });
        Object.defineProperty(this, "selectHeaderCell", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observer((props) => {
                if (this.props.hideSelectAll || this.tableState.selectionLimit !== Infinity) {
                    return null;
                }
                return (_jsx(SelectHeaderCell, Object.assign({}, props, { isSomeRowsSelected: this.props.scrollable !== 'virtual'
                        ? this.tableState.isSomePageRowsSelected
                        : this.tableState.isSomeRowsSelected, control: this.props.selectionControl })));
            })
        });
        // TODO: rid of "memoizeOne" after migration on React.FC
        Object.defineProperty(this, "withCellTableState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: memoizeOne((children) => this.applyToColumns(children, column => {
                const { cell: Cell } = column.props;
                return cloneElement(column, {
                    cell: Cell &&
                        ((props) => (_jsx(Cell, Object.assign({}, props, { tableState: this.tableState })))),
                });
            }))
        });
        Object.defineProperty(this, "rowRender", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (row, rowProps) => {
                const overrides = {};
                if (this.props.selectable &&
                    rowProps.rowType === 'data' &&
                    !this.tableState.isRowSelectable(rowProps.dataItem)) {
                    overrides.className = classNames(row.props.className, Styles.disabled);
                }
                const result = Object.keys(overrides).length ? cloneElement(row, overrides) : row;
                return this.props.rowRender ? this.props.rowRender(result, rowProps) : result;
            }
        });
        Object.defineProperty(this, "handleHeaderSelectionChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (ev) => {
                const checked = ev.syntheticEvent.currentTarget.checked;
                if (this.props.scrollable !== 'virtual') {
                    if (checked) {
                        this.tableState.selectPage();
                    }
                    else {
                        this.tableState.deselectPage();
                    }
                }
                else {
                    if (checked) {
                        this.tableState.selectAll();
                    }
                    else {
                        this.tableState.deselectAll();
                    }
                }
            }
        });
        Object.defineProperty(this, "handleSelectionChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (ev) => {
                var _a, _b, _c, _d;
                if (!ev.dataItem) {
                    return;
                }
                if (ev.nativeEvent.shiftKey && this.lastSelection) {
                    const rows = this.tableState
                        .getRowsBetween(this.lastSelection, ev.dataItem)
                        .filter(row => this.tableState.isRowSelectable(row));
                    const isAsc = ((_b = (_a = this.tableState.dataSource) === null || _a === void 0 ? void 0 : _a.idSelector) === null || _b === void 0 ? void 0 : _b.call(_a, this.lastSelection)) ===
                        ((_d = (_c = this.tableState.dataSource) === null || _c === void 0 ? void 0 : _c.idSelector) === null || _d === void 0 ? void 0 : _d.call(_c, rows[0]));
                    const selection = !ev.dataItem.selected;
                    if (selection) {
                        const toBeSelected = [];
                        for (const row of isAsc ? rows : rows.reverse()) {
                            if (this.tableState.selectedCount + toBeSelected.length >=
                                this.tableState.selectionLimit) {
                                break;
                            }
                            if (row.selected) {
                                continue;
                            }
                            toBeSelected.push(row);
                        }
                        this.tableState.setRowsSelection(toBeSelected, selection);
                    }
                    else {
                        this.tableState.setRowsSelection(rows, selection);
                    }
                }
                else {
                    this.tableState.toggleRowSelection(ev.dataItem);
                }
                this.lastSelection = ev.dataItem;
            }
        });
        Object.defineProperty(this, "scrollToBottom", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.ref.current) {
                    const container = this.ref.current.getElementsByClassName('k-grid-content')[0];
                    container.scrollTop = container.scrollHeight - container.clientHeight * 2;
                    container.scroll({ top: container.scrollHeight, behavior: 'smooth' });
                }
            }
        });
        Object.defineProperty(this, "table", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const _a = this.props, { className, filterOperators, selectable, pager } = _a, props = __rest(_a, ["className", "filterOperators", "selectable", "pager"]);
                return (_jsxs(AnvilTable, Object.assign({ ref: this.ref }, props, { data: [...this.tableState.data], rowRender: this.rowRender, editField: "inEdit", filter: this.tableState.filter, filterOperators: filterOperators !== null && filterOperators !== void 0 ? filterOperators : operators, onFilterChange: this.tableState.handleFilterChange, group: [...this.tableState.group], onGroupChange: this.tableState.handleGroupChange, onExpandChange: this.tableState.handleExpandChange, expandField: "expanded", sort: [...this.tableState.sort], onSortChange: this.tableState.handleSortChange, onHeaderSelectionChange: this.handleHeaderSelectionChange, onSelectionChange: this.handleSelectionChange, selectedField: "selected", pageable: this.getPageable(), pageSize: this.tableState.pageSize, skip: this.tableState.skip, onPageChange: this.tableState.handlePageChange, total: this.tableState.filteredCount, className: classNames(Styles.table, className), selectable: { enabled: selectable, mode: 'multiple' } }, { children: [selectable && (_jsx(TableColumn, { locked: true, field: "selected", width: "50px", filterable: false, resizable: false, headerSelectionValue: props.scrollable !== 'virtual'
                                ? this.tableState.isAllPageRowsSelected
                                : this.tableState.isAllRowsSelected, headerCell: this.selectHeaderCell, cell: this.selectColumnCell, headerClassName: "lh-1-i ta-center-i", className: classNames(Styles.unselectable, 'lh-1-i', 'ta-center-i') })), this.withCellTableState(props.children)] })));
            }
        });
    }
    applyToColumns(children, transformer) {
        return Children.map(children, child => {
            if (!isColumnElement(child)) {
                return child;
            }
            return child.props.children
                ? cloneElement(transformer(child), {
                    children: this.applyToColumns(child.props.children, transformer),
                })
                : transformer(child);
        });
    }
    render() {
        if (this.tableState.dataSource instanceof AsyncDataSource &&
            this.props.scrollable === 'virtual' &&
            this.props.selectable) {
            return (_jsx(Announcement, { status: "critical", title: "AsyncDataSource doesn't support virtual scroll with selection." }));
        }
        const table = this.table();
        return (_jsxs(Fragment, { children: [table, this.props.exportable && (_jsxs(Fragment, { children: [_jsxs(TablePDFExport, Object.assign({ ref: this.tableState.setTablePdfExportRef, fileName: sanitizeExportFileName(this.props.exportFileName) }, { children: [this.props.children, table] })), _jsx(ExcelExport, Object.assign({ ref: this.tableState.setTableExcelExportRef, fileName: sanitizeExportFileName(this.props.exportFileName) }, { children: this.props.children }))] }))] }));
    }
    getPageable() {
        var _a;
        if (this.props.scrollable === 'virtual' || !this.tableState.pageSize) {
            return false;
        }
        return (_a = this.props.pager) !== null && _a !== void 0 ? _a : true;
    }
};
Table = __decorate([
    observer
], Table);
export { Table };
const sanitizeExportFileName = memoizeOne((fileName) => fileName === null || fileName === void 0 ? void 0 : fileName.replace(/\.+/g, '_'));
//# sourceMappingURL=table.js.map